import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { trpcClient } from '../lib/trpc';

export interface PresenceState {
  onlineUserIds: number[];
  /** contextKey (e.g. a thread id) -> userIds currently focused on it. */
  usersByContext: Record<string, number[]>;
}

type Listener = (state: PresenceState) => void;

export interface WorkspaceSyncEvent {
  type: string;
  payload?: {
    users?: number[];
    byContext?: Record<string, number[]>;
    [key: string]: unknown;
  };
  userId?: number;
}

type EventListener = (event: WorkspaceSyncEvent) => void;

interface Connection {
  ws: WebSocket | null;
  listeners: Set<Listener>;
  eventListeners: Set<EventListener>;
  state: PresenceState;
  userId: number | null;
  heartbeat: ReturnType<typeof setInterval> | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  refCount: number;
}

const EMPTY_STATE: PresenceState = { onlineUserIds: [], usersByContext: {} };
const connections = new Map<string, Connection>();

function getWsBaseUrl() {
  const metaEnv = (import.meta as unknown as { env?: { VITE_WORKER_URL?: string } }).env;
  const workerUrl = metaEnv?.VITE_WORKER_URL || '';
  return workerUrl.replace(/^http/, 'ws');
}

/**
 * Mints the short-lived token that authorizes this socket.
 *
 * Minted per connection rather than cached: the token is deliberately short-lived,
 * and this socket reconnects on every drop, so a cached one would eventually be
 * expired at exactly the moment it is needed.
 */
async function mintPresenceToken(): Promise<string | null> {
  try {
    const { token } = await trpcClient.workspace.getPresenceToken.mutate();
    return token;
  } catch (e) {
    console.error('[useWorkspacePresence] Could not mint presence token:', e);
    return null;
  }
}

async function connect(workspaceId: number, conn: Connection) {
  const token = await mintPresenceToken();
  if (!token) {
    // Not entitled to this workspace (or offline). Retry on the same cadence as a
    // dropped socket rather than hammering the mint endpoint.
    if (conn.refCount > 0) {
      conn.reconnectTimer = setTimeout(() => connect(workspaceId, conn), 3000);
    }
    return;
  }

  // Released while the token request was in flight — don't open an orphan socket.
  if (conn.refCount <= 0) return;

  const wsUrl = `${getWsBaseUrl()}/ws/${workspaceId}?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(wsUrl);
  conn.ws = ws;

  ws.onopen = () => {
    // No userId: the server binds identity from the token and ignores anything the
    // client claims here.
    ws.send(JSON.stringify({ type: 'JOIN_CONTEXT', payload: {} }));
    conn.heartbeat = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'HEARTBEAT' }));
      }
    }, 30000);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'JOIN_CONTEXT' && data.payload) {
        conn.state = {
          onlineUserIds: data.payload.users || [],
          usersByContext: data.payload.byContext || {},
        };
        conn.listeners.forEach((listener) => {
          listener(conn.state);
        });
      }

      // Send other sync events (e.g. comment created/updated/deleted)
      conn.eventListeners.forEach((listener) => {
        try {
          listener(data);
        } catch (e) {
          console.error('[useWorkspacePresence] Error in event listener:', e);
        }
      });
    } catch (e) {
      console.error('[useWorkspacePresence] Error parsing message:', e);
    }
  };

  ws.onclose = () => {
    if (conn.heartbeat) clearInterval(conn.heartbeat);
    if (conn.refCount > 0) {
      conn.reconnectTimer = setTimeout(() => connect(workspaceId, conn), 3000);
    }
  };

  ws.onerror = (err) => {
    console.error(`[useWorkspacePresence] WebSocket error for workspace: ${workspaceId}:`, err);
    ws.close();
  };
}

function acquireConnection(workspaceId: number, userId: number | null): Connection {
  const key = String(workspaceId);
  let conn = connections.get(key);

  if (!conn) {
    conn = {
      ws: null,
      listeners: new Set(),
      eventListeners: new Set(),
      state: EMPTY_STATE,
      userId,
      heartbeat: null,
      reconnectTimer: null,
      refCount: 0,
    };
    connections.set(key, conn);
    connect(workspaceId, conn);
  }

  conn.refCount += 1;
  if (userId && conn.userId !== userId) {
    conn.userId = userId;
  }

  return conn;
}

function releaseConnection(workspaceId: number) {
  const key = String(workspaceId);
  const conn = connections.get(key);
  if (!conn) return;

  conn.refCount -= 1;
  if (conn.refCount <= 0) {
    if (conn.heartbeat) clearInterval(conn.heartbeat);
    if (conn.reconnectTimer) clearTimeout(conn.reconnectTimer);
    conn.ws?.close();
    connections.delete(key);
  }
}

/** Read-only subscription to live workspace presence (shares one socket per workspace across callers). */
export function useWorkspacePresence(
  workspaceId: number | null,
  userId: number | null
): PresenceState {
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!workspaceId) return () => {};

      const conn = acquireConnection(workspaceId, userIdRef.current);
      conn.listeners.add(onStoreChange);

      return () => {
        conn.listeners.delete(onStoreChange);
        releaseConnection(workspaceId);
      };
    },
    [workspaceId]
  );

  const getSnapshot = useCallback(() => {
    if (!workspaceId) return EMPTY_STATE;
    return connections.get(String(workspaceId))?.state ?? EMPTY_STATE;
  }, [workspaceId]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Announces that the current user is actively focused on `contextKey` (e.g. an open thread) while mounted. */
export function useAnnouncePresence(
  workspaceId: number | null,
  userId: number | null,
  contextKey: string | null
) {
  const sentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !userId || !contextKey) return;

    const conn = acquireConnection(workspaceId, userId);

    const send = () => {
      if (conn.ws?.readyState === WebSocket.OPEN) {
        // Announces *where* this socket is. Who it belongs to is fixed by the token
        // it connected with, so no userId travels with it.
        conn.ws.send(JSON.stringify({ type: 'JOIN_CONTEXT', payload: { url: contextKey } }));
        sentRef.current = contextKey;
      }
    };

    send();
    const retry = setInterval(() => {
      if (sentRef.current !== contextKey) send();
    }, 1000);

    return () => {
      clearInterval(retry);
      // Clear this user's focus on the context by re-announcing with none.
      if (conn.ws?.readyState === WebSocket.OPEN) {
        conn.ws.send(JSON.stringify({ type: 'JOIN_CONTEXT', payload: {} }));
      }
      releaseConnection(workspaceId);
    };
  }, [workspaceId, userId, contextKey]);
}

/** Hook to subscribe to live custom workspace events. */
export function useWorkspaceEvent(
  workspaceId: number | null,
  userId: number | null,
  onEvent: (event: WorkspaceSyncEvent) => void
) {
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!workspaceId) return;

    const conn = acquireConnection(workspaceId, userId);
    const listener = (event: WorkspaceSyncEvent) => {
      onEventRef.current(event);
    };
    conn.eventListeners.add(listener);

    return () => {
      conn.eventListeners.delete(listener);
      releaseConnection(workspaceId);
    };
  }, [workspaceId, userId]);
}
